import { InputField, SelectField, SelectOptionType } from "@/components";
import { PAGINATION } from "@/constants/common";
import {
  ACTION,
  optionsSpecificDay,
  optionsTimeBand,
  SCHEDULE_MODE,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import { DataType } from "@/partial/template/types";
import { useTemplateList } from "@/services/hooks/template/useTemplate";
import useCampaignStore from "@/store/campaignStore";
import { useEffect, useState } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { Fragment } from "react/jsx-runtime";

interface TemplateProps {
  isEdit: boolean;
  indexTemplate: number;
  indexChannel: number;
  control: Control<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
}

export const Template = ({
  isEdit,
  indexTemplate,
  indexChannel,
  control,
  setValue,
}: TemplateProps) => {
  const { channelParametersOptions: channelParametersOptionsStore } =
    useCampaignStore();

  const [templateScriptOptions, setTemplateScriptOptions] = useState<
    SelectOptionType[]
  >([]);

  const [params, setParams] = useState<{
    page: number;
    limit: number;
    search: string;
    filter?: string;
  }>({
    page: PAGINATION.MIN_PAGE,
    limit: PAGINATION.MAX_LIMIT,
    search: "",
  });

  const { data: templateList, refetch } = useTemplateList(params);

  const dataSource = useWatch({
    control,
    name: "data_source",
  });

  const channel = useWatch({
    control,
    name: `campaign_channels[${indexChannel}].channel` as `campaign_channels.${number}.channel`,
  });

  const isSpecificDay = useWatch({
    control,
    name: `campaign_channels[${indexChannel}].is_check_day` as `campaign_channels.${number}.is_check_day`,
    defaultValue: SCHEDULE_MODE.SPECIFIC_DATE,
  });

  const action = useWatch({
    control,
    name: `campaign_channels[${indexChannel}].action` as `campaign_channels.${number}.action`,
  });

  useEffect(() => {
    if (dataSource?.[0]?.value && channel) {
      const filterObj = {
        data_source: dataSource[0].value,
        channel: channel,
      };
      setParams((prev) => ({
        ...prev,
        filter: JSON.stringify(filterObj),
      }));
    }
  }, [dataSource, channel]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (isEdit && channelParametersOptionsStore) {
      const templateScriptId =
        channelParametersOptionsStore?.[indexChannel]?.campaignDataChannels?.[
          indexTemplate
        ]?.campaignTemplate;
      if (templateScriptId) {
        setValue(
          `campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].template_script_id` as `campaign_channels.${number}.campaign_data_channels.${number}.template_script_id`,
          [templateScriptId]
        );
      } else {
        setValue(
          `campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].template_script_id` as `campaign_channels.${number}.campaign_data_channels.${number}.template_script_id`,
          []
        );
      }
    }
  }, [
    isEdit,
    setValue,
    indexChannel,
    indexTemplate,
    channelParametersOptionsStore,
  ]);

  useEffect(() => {
    if (templateList?.data?.response) {
      const templateOptions = templateList.data.response.map(
        (item: DataType) => ({
          label: item.name,
          value: item.id,
        })
      );

      let finalTemplateOptions = [...templateOptions];

      if (isEdit && channelParametersOptionsStore) {
        const selectedTemplatesInEditMode =
          channelParametersOptionsStore[indexChannel]?.campaignDataChannels?.[
            indexTemplate
          ]?.campaignTemplate;

        if (
          Array.isArray(selectedTemplatesInEditMode) &&
          selectedTemplatesInEditMode.length > 0
        ) {
          const templateOptionValues = new Set(
            templateOptions.map((opt: SelectOptionType) => opt.value)
          );

          const uniqueSelectedTemplates = selectedTemplatesInEditMode.filter(
            (selectedOpt: SelectOptionType) =>
              !templateOptionValues.has(selectedOpt.value)
          );

          finalTemplateOptions = [
            ...uniqueSelectedTemplates,
            ...templateOptions,
          ];
        }
      }

      setTemplateScriptOptions(finalTemplateOptions);
    } else {
      setTemplateScriptOptions([]);
    }
  }, [
    isEdit,
    indexChannel,
    indexTemplate,
    templateList,
    channelParametersOptionsStore,
  ]);

  useEffect(() => {
    if (action && action[0]?.value === ACTION.ON_DATE) {
      setValue(
        `campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].day` as `campaign_channels.${number}.campaign_data_channels.${number}.day`,
        [] as SelectOptionType[]
      );
      setValue(
        `campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].number_of_days` as `campaign_channels.${number}.campaign_data_channels.${number}.number_of_days`,
        undefined
      );
    }
  }, [action, indexChannel, indexTemplate, setValue]);

  const renderScheduleMode = () => {
    return (
      <Fragment>
        {/* if day mode = specific date  */}
        {isSpecificDay === SCHEDULE_MODE.SPECIFIC_DATE && (
          <SelectField
            name={`campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].day`}
            label="Day"
            isRequired={action ? action[0]?.value !== ACTION.ON_DATE : true}
            control={control}
            placeholder="Select Day"
            options={optionsSpecificDay}
            withCheckbox
            isMultiple
            disabled={action ? action[0]?.value === ACTION.ON_DATE : false}
          />
        )}
        {/* if day mode = number of days */}
        {isSpecificDay === SCHEDULE_MODE.NUMBER_OF_DAYS && (
          <InputField
            name={`campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].number_of_days`}
            label="Number of Day"
            placeholder="Enter Number of Day"
            control={control}
            type="number"
            isRequired={action ? action[0]?.value !== ACTION.ON_DATE : true}
            disabled={action ? action[0]?.value === ACTION.ON_DATE : false}
          />
        )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      {renderScheduleMode()}
      <SelectField
        name={`campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].time_band`}
        label="Time Band"
        placeholder="Select Time Band"
        isRequired
        control={control}
        options={optionsTimeBand}
        withCheckbox
        isMultiple
      />
      <SelectField
        name={`campaign_channels[${indexChannel}].campaign_data_channels[${indexTemplate}].template_script_id`}
        label="Template Script"
        placeholder="Select Template Script"
        isRequired
        control={control}
        options={templateScriptOptions}
        withSearch
      />
    </Fragment>
  );
};

export default Template;
